import type {
  ContourId,
} from '@/src/store/contours/contour-definitions';
import type {
  ContourPropagationOptions,
  FrameDataInput,
} from '@/src/store/contours/contour-processing/contourPropagation';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { useAlert } from '@/src/composables/useAlert';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import {
  ContourType,
} from '@/src/store/contours/contour-definitions';
import { propagateAllPhases } from '@/src/store/contours/contour-processing/contourPropagation';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

export async function useContourTracing(
  viewerID: string,
  currentImageUID: string,
  targetContourId?: ContourId,
  options?: ContourPropagationOptions,
) {
  const currentStudyStore = useCurrentStudyStore();
  const contourContainerStore = useContourContainerStore();
  const viewportStateStore = useViewportStateStore();
  const alert = useAlert();

  // 1. Determine target contour ID (from argument or viewport selection)
  let contourId = targetContourId;
  if (!contourId || !contourId.label || !contourId.id) {
    const selected = viewportStateStore.getSelectedContour(viewerID);
    if (selected && selected.label && selected.id) {
      contourId = {
        label: selected.label,
        id: selected.id,
      };
    }
  }

  if (!contourId || !contourId.label || !contourId.id) {
    alert.warning('Cannot run trace. Please select a contour to trace.');
    return;
  }

  // 2. Fetch the reference contour from the current image
  const currentContours = contourContainerStore.getDrawingContours(currentImageUID);
  if (!currentContours || !currentContours[contourId.label] || !currentContours[contourId.label][contourId.id]) {
    alert.warning(
      `Selected contour (${contourId.label}) was not found on the current image.`,
    );
    return;
  }

  const targetContour = currentContours[contourId.label][contourId.id];
  const referencePolygon = targetContour.getPolygonScaled();
  if (!referencePolygon || referencePolygon.length < 3) {
    alert.warning('Selected contour must have at least 3 points to trace.');
    return;
  }

  const isOpenContour = targetContour.getProps().type === ContourType.PolygonOpenContour;
  const contourDisplayLabel
    = targetContour.getProps().displayLabel || contourId.label;

  // 3. Resolve cine frame UIDs for this slice / view
  const studyObj = useViewportActions().getStudyObjectByViewerId(viewerID);
  const breathholdState = studyObj?.getBreathhold();
  const start = breathholdState?.getStart();
  const end = breathholdState?.getEnd();

  const imageUIDsfromStore = currentStudyStore.getSliceImageUIDsbyView(viewerID);
  if (!imageUIDsfromStore || imageUIDsfromStore.length === 0) {
    alert.error('Cannot run trace. No images found for the current view.');
    return;
  }

  const croppedImageUIDs = imageUIDsfromStore.slice(
    start,
    end !== undefined && end !== null ? end + 1 : undefined,
  );

  const startingImageIndex = croppedImageUIDs.indexOf(currentImageUID);
  if (startingImageIndex === -1) {
    alert.error('Cannot run trace. Current image UID not found in the cine sequence.');
    return;
  }

  if (croppedImageUIDs.length < 2) {
    alert.warning('Cannot run trace. Cine sequence requires at least 2 frames.');
    return;
  }

  const startTime = performance.now();

  // 4. Fetch VTK image data for all cine frames
  const frames: FrameDataInput[] = [];
  for (const imageUID of croppedImageUIDs) {
    const imageMeta = await currentStudyStore.fetchOrGetVtkImageData(imageUID);
    if (!imageMeta) {
      alert.error(
        'Cannot run trace. Unable to retrieve image data for one or more cine frames.',
      );
      return;
    }
    const rawData = imageMeta.getPointData().getScalars().getData();
    const data
      = rawData instanceof Float32Array
        ? rawData
        : Float32Array.from(rawData as ArrayLike<number>);
    const dims = imageMeta.getDimensions(); // [width, height, depth]
    frames.push({
      data,
      width: dims[0],
      height: dims[1],
    });
  }

  // 5. Run normal-profile contour propagation across all phases
  const { contours: propagatedContours } = propagateAllPhases(
    frames,
    referencePolygon,
    startingImageIndex,
    options,
    isOpenContour,
  );

  // 6. Save propagated contours into the store in a single batch
  const batchContours = [];
  for (let i = 0; i < croppedImageUIDs.length; i++) {
    const frameUID = croppedImageUIDs[i];
    // Keep user's exact original polygon on the reference frame, propagated polygons on others
    const points = i === startingImageIndex ? referencePolygon : propagatedContours[i];
    batchContours.push({
      UID: frameUID,
      points,
      label: contourId.label,
      id: contourId.id,
      replace: true,
    });
  }

  contourContainerStore.addContoursBatch(batchContours);

  const endTime = performance.now();
  const durationSec = ((endTime - startTime) / 1000).toFixed(2);
  alert.success(
    `Contour tracing for "${contourDisplayLabel}" completed across ${croppedImageUIDs.length} frames in ${durationSec}s!`,
  );
}
